import {
  activatePlatformEmailTemplateVersion,
  fetchPlatformEmailTemplates,
  previewPlatformEmailTemplate,
  savePlatformEmailTemplateDraft,
} from "./platformEmailApi.mjs";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      activating: false,
      previewing: false,
      previewOpen: false,
      templates: [],
      selectedTemplateKey: "registration.verify_email",
      selectedLocale: "ro",
      selectedVersion: null,
      editor: { subject: "", textBody: "", htmlBody: "" },
      preview: null,
      errorMessage: "",
      successMessage: "",
    };
  },
  computed: {
    templateItems() {
      return this.templates.map((template) => ({
        title: this.templateLabel(template),
        value: template.templateKey,
      }));
    },
    localeItems() {
      return ["ro", "en", "ru"].map((locale) => ({
        title: this.$t(`admin.platformEmail.templates.locales.${locale}`),
        value: locale,
      }));
    },
    selectedTemplate() {
      return this.templates.find((template) => template.templateKey === this.selectedTemplateKey) || null;
    },
    versions() {
      return this.selectedTemplate?.locales?.[this.selectedLocale] || [];
    },
    versionItems() {
      return this.versions.map((version) => ({
        title: `v${version.version} · ${this.$t(`admin.platformEmail.templates.statuses.${version.status}`)}`,
        value: version.version,
      }));
    },
    currentVersion() {
      return this.versions.find((version) => version.version === this.selectedVersion) || null;
    },
    activeVersion() {
      return this.versions.find((version) => version.status === "active") || null;
    },
    allowedVariables() {
      return this.selectedTemplate?.allowedVariables || [];
    },
    canActivate() {
      return Boolean(this.currentVersion) && this.currentVersion.status !== "active";
    },
  },
  watch: {
    selectedTemplateKey() {
      this.selectDefaultVersion();
    },
    selectedLocale() {
      this.selectDefaultVersion();
    },
    selectedVersion() {
      this.loadVersionIntoEditor();
    },
  },
  mounted() {
    this.loadTemplates();
  },
  methods: {
    formatVariable(variable) {
      return `{{${variable}}}`;
    },
    templateLabel(template) {
      const key = `admin.platformEmail.templates.names.${template?.nameKey || ""}`;
      const translated = this.$t(key);
      return translated === key ? template?.templateKey : translated;
    },
    clearFeedback() {
      this.errorMessage = "";
      this.successMessage = "";
    },
    selectDefaultVersion(preferredVersion = null) {
      const versions = this.selectedTemplate?.locales?.[this.selectedLocale] || [];
      const preferred = versions.find((version) => version.version === preferredVersion);
      const active = versions.find((version) => version.status === "active");
      this.selectedVersion = (preferred || active || versions[0])?.version || null;
      this.loadVersionIntoEditor();
    },
    loadVersionIntoEditor() {
      const version = this.currentVersion;
      this.editor = {
        subject: version?.subject || "",
        textBody: version?.textBody || "",
        htmlBody: version?.htmlBody || "",
      };
    },
    async loadTemplates(preferredVersion = null) {
      this.loading = true;
      this.clearFeedback();
      try {
        const { data } = await fetchPlatformEmailTemplates();
        this.templates = Array.isArray(data?.templates) ? data.templates : [];
        if (!this.templates.some((item) => item.templateKey === this.selectedTemplateKey)) {
          this.selectedTemplateKey = this.templates[0]?.templateKey || "";
        }
        this.selectDefaultVersion(preferredVersion);
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.platformEmail.errors.loadTemplates");
      } finally {
        this.loading = false;
      }
    },
    async saveDraft() {
      this.saving = true;
      this.clearFeedback();
      try {
        const { data } = await savePlatformEmailTemplateDraft({
          templateKey: this.selectedTemplateKey,
          locale: this.selectedLocale,
          ...this.editor,
        });
        await this.loadTemplates(data?.template?.version || null);
        this.successMessage = this.$t("admin.platformEmail.messages.templateSaved");
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.platformEmail.errors.saveTemplate");
      } finally {
        this.saving = false;
      }
    },
    async activateVersion() {
      if (!this.currentVersion) return;
      this.activating = true;
      this.clearFeedback();
      try {
        await activatePlatformEmailTemplateVersion({
          templateKey: this.selectedTemplateKey,
          locale: this.selectedLocale,
          version: this.currentVersion.version,
        });
        await this.loadTemplates(this.currentVersion?.version || null);
        this.successMessage = this.$t("admin.platformEmail.messages.templateActivated");
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.platformEmail.errors.activateTemplate");
      } finally {
        this.activating = false;
      }
    },
    async openPreview() {
      this.previewing = true;
      this.clearFeedback();
      try {
        const { data } = await previewPlatformEmailTemplate({
          templateKey: this.selectedTemplateKey,
          locale: this.selectedLocale,
          ...this.editor,
        });
        this.preview = data?.preview || null;
        this.previewOpen = true;
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || this.$t("admin.platformEmail.errors.previewTemplate");
      } finally {
        this.previewing = false;
      }
    },
  },
};
